import { useEffect, useRef } from 'react';

/**
 * Scroll-reveal hook. Attach the returned ref to a container; every descendant
 * carrying the `.reveal` class gets `.is-revealed` toggled once it scrolls into
 * view, driving the CSS reveal-up animation (respects prefers-reduced-motion).
 *
 * @param {Object} [opts]
 * @param {string} [opts.selector='.reveal'] elements to observe within the container
 * @param {number} [opts.threshold=0.12]
 * @param {boolean} [opts.once=true] stop observing after first reveal
 */
export default function useReveal({ selector = '.reveal', threshold = 0.12, once = true } = {}) {
  const ref = useRef(null);

  useEffect(() => {
    const root = ref.current;
    if (!root) return undefined;

    const targets = Array.from(root.querySelectorAll(selector));
    if (targets.length === 0) return undefined;

    // No IntersectionObserver (or reduced motion handled by CSS) → show immediately.
    if (typeof IntersectionObserver === 'undefined') {
      targets.forEach((el) => el.classList.add('is-revealed'));
      return undefined;
    }

    const observer = new IntersectionObserver(
      (entries, obs) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('is-revealed');
            if (once) obs.unobserve(entry.target);
          }
        });
      },
      { threshold, rootMargin: '0px 0px -8% 0px' }
    );

    targets.forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, [selector, threshold, once]);

  return ref;
}
